// Списъкът с чакащите отчети (лявата колона на „Разнасяне на отчети"; на
// тесен екран — хоризонтална лента над разнасянето). Най-старият е най-отгоре.
// Картата е на ДВА реда (~60px): „№X · дата · [възраст]" и „сметки · оборот ·
// ● статус" (кратко — „6 без склад", „3 с недостиг"; пълното е в подсказката;
// „без склад" е кехлибарено, недостигът — червено, „Всичко е налично" —
// зелено). Два статуса наведнъж („● 1 без склад · ● 3 с недостиг") минават на
// трети ред. Закъснелият (над
// OVERDUE_REPORT_DAYS) носи червена значка — критичен сигнал. Изборът СЛЕДВА
// фокуса: ↑/↓ (←/→ в лентата), Home/End — само докато фокусът е на карта.
//
// В колоната избраната карта се РАЗГЪВА: под нея стои `selectedExtra`
// (разбивката по сервитьори и станции — `PendingReportBreakdown`), в същата
// рамка. Невзбраните са компактни. Затова списъкът е група бутони с
// `aria-current`, не listbox: listbox не може да носи други бутони вътре.
import { useRef, type ReactNode } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { PendingReport } from "../../../../api/inventory";
import { formatMoney } from "../../components/numbers";
import { StatusDot } from "../../components/list/ListBits";
import { SIGNAL_BADGE_CLASS } from "../../components/list/signals";
import { isReportOverdue, reportAgeDays } from "./pendingReportAge";
import type { PostingStatusPart } from "./pendingReportStatus";

const STATUS_TEXT_CLASS = {
  danger: "text-danger-ink",
  ok: "text-ok-deep",
  brand: "text-brand-deep-ink",
} as Partial<Record<PostingStatusPart["tone"], string>>;

// Статусът на картата: точка + КРАТКИЯТ текст по тон („6 без склад", „3 с
// недостиг" — пълният е в подсказката); при `compact` (два статуса на собствен
// ред) — без водещата точка-разделител
const StatusLine = ({ parts, compact = false }: { parts: PostingStatusPart[]; compact?: boolean }) => (
  <span
    title={parts.map((part) => part.text).join(" · ")}
    className="flex min-w-0 items-center gap-1.5 overflow-hidden font-medium"
  >
    {parts.map((part, index) => (
      <span
        key={part.tone}
        className={`flex min-w-0 items-center gap-1.5 ${STATUS_TEXT_CLASS[part.tone] ?? "text-body"} ${
          compact && index < parts.length - 1 ? "shrink-0" : ""
        }`}
      >
        {(!compact || index > 0) && (
          <span aria-hidden className="text-faint">
            ·
          </span>
        )}
        <StatusDot tone={part.tone} />
        <span className="min-w-0 truncate">{part.shortText ?? part.text}</span>
      </span>
    ))}
  </span>
);

type Props = {
  reports: PendingReport[];
  selectedId: number | null;
  onSelect: (reportId: number) => void;
  statusOf: (report: PendingReport) => PostingStatusPart[] | null;
  orientation: "vertical" | "horizontal";
  // под избраната карта (само в колоната)
  selectedExtra?: ReactNode;
};

export const PendingReportList = ({
  reports,
  selectedId,
  onSelect,
  statusOf,
  orientation,
  selectedExtra,
}: Props) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement | null>(null);
  const horizontal = orientation === "horizontal";

  const focusIndex = (index: number) => {
    const target = reports[index];
    if (!target) return;
    onSelect(target.id);
    listRef.current
      ?.querySelector<HTMLButtonElement>(`[data-report="${target.id}"]`)
      ?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    // стрелките в разбивката под картата не сменят отчета
    if (!(event.target as HTMLElement).dataset?.report) return;
    const index = reports.findIndex((report) => report.id === selectedId);
    const back = horizontal ? "ArrowLeft" : "ArrowUp";
    const forward = horizontal ? "ArrowRight" : "ArrowDown";
    let next: number | null = null;
    if (event.key === forward) next = Math.min(index + 1, reports.length - 1);
    else if (event.key === back) next = Math.max(index - 1, 0);
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = reports.length - 1;
    if (next == null) return;
    event.preventDefault();
    focusIndex(next);
  };

  return (
    <div
      ref={listRef}
      role="group"
      aria-label={t("inventoryPage.postingPendingList")}
      onKeyDown={onKeyDown}
      className={`flex shrink-0 gap-1.5 overflow-auto bg-fill p-3 ${
        horizontal
          ? "w-full flex-row border-b border-shade-2"
          : "w-[264px] flex-col border-r border-shade-2"
      }`}
    >
      {reports.map((report) => {
        const active = report.id === selectedId;
        // В колоната избраната карта е в обща рамка с разбивката под нея
        const expanded = active && !horizontal;
        const age = reportAgeDays(report.reportDate);
        const overdue = isReportOverdue(report.reportDate);
        const ageLabel =
          age <= 0
            ? t("inventoryPage.postingAgeToday")
            : age === 1
              ? t("inventoryPage.postingAgeYesterday")
              : overdue
                ? t("inventoryPage.postingAgeLate", { count: age })
                : t("inventoryPage.postingAgeDays", { count: age });
        const statusParts = statusOf(report);
        const frameClass = active
          ? "border-brand bg-surface shadow-[0_0_0_3px_var(--color-brand-tint)]"
          : "border-shade-2 bg-panel hover:border-brand/45";
        const card = (
          <button
            key={report.id}
            type="button"
            aria-current={active ? "true" : undefined}
            data-report={report.id}
            tabIndex={active ? 0 : -1}
            onClick={() => onSelect(report.id)}
            className={`flex shrink-0 cursor-pointer flex-col gap-1 px-3 py-2 text-left text-ink outline-none pointer-coarse:py-3 ${
              horizontal ? "w-[240px]" : "w-full"
            } ${
              expanded
                ? "rounded-t-[10px] border-none bg-transparent focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)]"
                : `rounded-[10px] border focus-visible:border-brand ${frameClass}`
            }`}
          >
            <span className="flex min-w-0 items-center gap-1.5 leading-5">
              <span className="shrink-0 text-[14px] font-semibold">
                {t("inventoryPage.postingCardNumber", { number: report.reportNumber })}
              </span>
              <span className="min-w-0 truncate text-[12.5px] text-muted">
                · {dayjs(report.reportDate).format("DD.MM")}
              </span>
              <span
                className={`ml-auto shrink-0 whitespace-nowrap rounded-full px-2 text-[11.5px] font-medium leading-[18px] ${
                  overdue ? SIGNAL_BADGE_CLASS.critical : "bg-fill-2 text-body"
                }`}
              >
                {ageLabel}
              </span>
            </span>
            <span className="flex min-w-0 items-center gap-1.5 text-[12px] leading-[18px] text-body">
              <span className="shrink-0">
                {t("inventoryPage.postingPickBills", { count: report.totalBillsCount })} ·{" "}
                <span className="font-sheet font-semibold text-ink">{formatMoney(report.totalAmount)}</span>
              </span>
              {statusParts && statusParts.length === 1 && <StatusLine parts={statusParts} />}
            </span>
            {/* Два статуса („без склад" и недостиг) не се побират до сумата —
                трети ред с кратките текстове */}
            {statusParts && statusParts.length > 1 && (
              <span className="flex min-w-0 text-[12px] leading-[18px]">
                <StatusLine parts={statusParts} compact />
              </span>
            )}
          </button>
        );
        if (!expanded) return card;
        return (
          <div
            key={report.id}
            data-report-card-expanded
            className={`flex w-full shrink-0 flex-col overflow-hidden rounded-[10px] border ${frameClass}`}
          >
            {card}
            {selectedExtra}
          </div>
        );
      })}
    </div>
  );
};

export default PendingReportList;
